import FormattedMessage from 'I18n/components/FormattedMessage';
import { H2 } from 'foundations-components/transitional/components/H2';
import { isFulfilled } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Flex } from 'foundations-components/components/Flex';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import ReviewsFilterSortDropdowns from 'marketplace-ui-reviews/components/ReviewsFilterSortDropdowns';
import ReviewsHeader from 'marketplace-ui-reviews/components/ReviewsHeader';
import ReviewsListStats from 'marketplace-ui-reviews/components/ReviewsListStats';
import ReviewsPaginatedList from 'marketplace-ui-reviews/components/ReviewsPaginatedList';
import { SEARCH_KEY } from 'marketplace-ui-reviews/constants/ReviewsListQueries';
import { useReviewability } from 'marketplace-ui-reviews/hooks/useReviewability';
import { useReviews } from 'marketplace-ui-reviews/hooks/useReviews';
import { getAppContextCoreType, getIsPublicApp } from '../../selectors/app';
import AssetReviewRenderer from './AssetReviewRenderer';
import { AssetReviewWizard } from './AssetReviewWizard';
import ReviewsListEmptyState from './ReviewsListEmptyState';
import WriteReviewButton from './WriteReviewButton';
import { useGetPreviousReviews } from 'marketplace-ui-reviews/hooks/useGetPreviousReviews';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const REVIEWS_LIST_WIDTH = 750;
export default function Reviews({
  name,
  exampleUrl,
  isPreview,
  offeringId,
  offeringPortalId,
  offeringType,
  onTrackInteraction,
  providerName,
  onReviewsFulfilled,
  reviewFilters,
  reviewType,
  supportUrl,
  thumbnailUrl,
  updateReviewFilters,
  versionNumber
}) {
  const appContext = useSelector(getAppContextCoreType);
  const {
    pathname
  } = useLocation();
  const isReviewLink = pathname.includes('/write-review');
  const [wizardOpen, setWizardOpen] = useState(isReviewLink);
  const [searchTerm, setSearchTerm] = useState('');
  const isPublicApp = useSelector(getIsPublicApp);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const {
    reviews: reviewsList,
    reviewStats,
    reviewsFetchStatus,
    reviewStatsFetchStatus,
    reviewsTotal,
    // based on filters
    reviewsCount // all reviews
  } = useReviews({
    appContext,
    reviewFilters,
    offeringId,
    reviewType
  });
  const {
    reviews: editableReviews
  } = useGetPreviousReviews({
    appContext,
    entityId: offeringId,
    reviewType
  });
  const editedReview = editableReviews[0];
  const {
    unreviewableReason,
    requestState: reviewabilityRequestStatus
  } = useReviewability({
    editPending: !!editedReview && reviewSubmitted,
    entityId: offeringId,
    entityPortalId: offeringPortalId,
    isPublicApp,
    reviewPending: !editedReview && reviewSubmitted,
    reviewType
  });
  const {
    maxWidth
  } = useResponsiveProps({
    maxWidth: {
      lg: REVIEWS_LIST_WIDTH
    }
  });
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(Object.assign({
      action
    }, eventProperties, {
      section: 'review'
    }));
  }, [onTrackInteraction]);

  // marketplace-ui-reviews uses a single object that contains all the properties.
  const trackReviewInteraction = useCallback(eventProperties => {
    onTrackInteraction(Object.assign({}, eventProperties, {
      section: 'review'
    }));
  }, [onTrackInteraction]);
  useEffect(() => {
    if (isFulfilled(reviewStatsFetchStatus) && isFulfilled(reviewsFetchStatus)) {
      onReviewsFulfilled(true);
    }
  }, [reviewStatsFetchStatus, reviewsFetchStatus, onReviewsFulfilled]);
  const onUpdateFilters = useCallback(({
    textKey,
    value
  }) => {
    if (textKey === SEARCH_KEY) {
      setSearchTerm(value);
    }
    updateReviewFilters({
      [textKey.toLowerCase()]: value
    });
  }, [setSearchTerm, updateReviewFilters]);
  const renderedWriteReviewButton = useMemo(() => /*#__PURE__*/_jsx(Flex, {
    marginBlock: "400",
    justifyContent: "end",
    children: /*#__PURE__*/_jsx(WriteReviewButton, {
      disabled: !!isPreview,
      hasEditableReview: !isPublicApp && !!editedReview,
      onClick: () => setWizardOpen(true),
      reviewPending: reviewSubmitted,
      unreviewableReason: unreviewableReason
    })
  }), [isPreview, isPublicApp, reviewSubmitted, unreviewableReason, editedReview]);
  const renderedReviewsList = useMemo(() => /*#__PURE__*/_jsxs("div", {
    style: {
      maxWidth
    },
    children: [/*#__PURE__*/_jsx(ReviewsHeader, {
      onUpdateFilters: onUpdateFilters,
      renderedButtonRow: renderedWriteReviewButton,
      reviewsCount: reviewStats.count || 0,
      reviewFilters: reviewFilters
    }), /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
      onUpdateFilters: onUpdateFilters,
      reviewFilters: reviewFilters,
      reviewStats: reviewStats
    }), /*#__PURE__*/_jsx(ReviewsPaginatedList, {
      onUpdateFilters: onUpdateFilters,
      ReviewRenderer: AssetReviewRenderer,
      reviews: reviewsList,
      reviewsFetchStatus: reviewsFetchStatus,
      reviewFilters: reviewFilters,
      reviewsTotal: reviewsTotal,
      searchTerm: searchTerm,
      reviewType: reviewType,
      onTrackInteraction: trackReviewInteraction
    })]
  }), [maxWidth, onUpdateFilters, renderedWriteReviewButton, reviewStats, reviewFilters, reviewsList, reviewsFetchStatus, reviewsTotal, searchTerm, reviewType, trackReviewInteraction]);
  const renderedReviewsBody = useMemo(() => {
    if (reviewsCount > 2) {
      return /*#__PURE__*/_jsxs(Grid, {
        marginBlockStart: "800",
        children: [/*#__PURE__*/_jsx(GridItem, {
          size: {
            xs: 12,
            lg: 4
          },
          children: /*#__PURE__*/_jsx(LoadingWrapper, {
            requestStatus: reviewStatsFetchStatus,
            children: /*#__PURE__*/_jsx(ReviewsListStats, {
              onUpdateFilters: onUpdateFilters,
              reviewStats: reviewStats,
              reviewType: reviewType
            })
          })
        }), /*#__PURE__*/_jsx(GridItem, {
          size: {
            xs: 12,
            lg: 8
          },
          children: renderedReviewsList
        })]
      });
    } else if (reviewsCount && reviewsCount <= 2) {
      return renderedReviewsList;
    } else if (!reviewsCount) {
      return /*#__PURE__*/_jsx(ReviewsListEmptyState, {
        reviewPending: reviewSubmitted,
        isPreview: !!isPreview,
        onWriteReviewClick: () => setWizardOpen(true),
        unreviewableReason: unreviewableReason
      });
    }
    return null;
  }, [isPreview, onUpdateFilters, renderedReviewsList, reviewsCount, reviewStats, reviewStatsFetchStatus, reviewSubmitted, reviewType, unreviewableReason]);
  const renderedWizard = useMemo(() => {
    return /*#__PURE__*/_jsx(LoadingWrapper, {
      requestStatus: reviewabilityRequestStatus,
      children: /*#__PURE__*/_jsx(AssetReviewWizard, {
        editedReview: editedReview,
        name: name,
        thumbnailUrl: thumbnailUrl,
        offeringId: offeringId,
        offeringType: offeringType,
        onClose: () => setWizardOpen(false),
        onSuccess: () => {
          setReviewSubmitted(true);
          setWizardOpen(false);
        },
        onTrackInteraction: trackInteraction,
        providerName: providerName,
        reviewType: reviewType,
        supportLink: supportUrl,
        slug: exampleUrl,
        unreviewableReason: unreviewableReason,
        versionNumber: versionNumber
      })
    });
  }, [exampleUrl, name, offeringId, offeringType, providerName, reviewabilityRequestStatus, supportUrl, thumbnailUrl, trackInteraction, unreviewableReason, editedReview, versionNumber, reviewType]);
  return /*#__PURE__*/_jsxs("div", {
    style: {
      position: 'relative'
    },
    children: [/*#__PURE__*/_jsx(H2, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-assets-ui.themes.details.reviews.title",
        options: {
          name
        }
      })
    }), renderedReviewsBody, wizardOpen && renderedWizard]
  });
}